import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import {
  SpeakerContactForm,
  SpeakerContactFormFieldset,
  SpeakerContactFormSubmit,
} from '@/components/ui/shared/profile/SpeakerContactForm/SpeakerContactForm';
import { useAnalytics } from '@/context/Analytics';
import { EmailSourceType } from '@/enums/ProfileContacts';
import { IToastType } from '@/enums/ToastType';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';

export const ContactProfileModal = ({ profile }) => {
  const analytics = useAnalytics();
  const { user } = useSession();
  const { t } = useTranslation();
  const toast = useToast();

  const { isOpen, onOpen: onOpenModal, onClose } = useDisclosure();

  const initialRef = useRef();

  const { id: lectureId } = useLecturePageQueryParams();

  const onOpen = () => {
    onOpenModal();

    analytics.track('library_lecture_speaker_email', {
      lecture_id: lectureId,
      speaker_name: profile?.fullName,
      speaker_id: profile.id,
    });
  };

  const onSubmitSuccess = () => {
    analytics.track('library_lecture_speaker_email_send', {
      lecture_id: lectureId,
      speaker_name: profile?.fullName,
      speaker_id: profile.id,
    });

    onClose();
    toast({ status: IToastType.SUCCESS, title: t('contactForm.notifications.success') });
  };

  const onSubmitFailure = () => {
    toast({ status: IToastType.ERROR, title: t('contactForm.notifications.error') });
  };

  return (
    <Fragment>
      <Button variant="unstyled" h="20px" mt={1} onClick={onOpen} textAlign="left">
        email
      </Button>

      <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent px={{ base: 4, md: 12 }} py={{ base: 6, md: 12 }} mx={{ base: 5, lg: 'unset' }}>
          <ModalHeader as={Heading} px={0} pb={{ base: 4, md: 8 }} fontSize={{ base: '2xl', md: '4xl' }}>
            {t('contactForm.titleDirect', { profileName: profile.fullName })}
          </ModalHeader>
          <SpeakerContactForm
            profile={profile}
            lectureId={lectureId}
            onSubmitSuccess={onSubmitSuccess}
            onSubmitFailure={onSubmitFailure}
            source={EmailSourceType.Library}
            defaultValues={{
              name: user?.fullName,
              email: user?.email,
            }}
          >
            <ModalBody p={0}>
              <SpeakerContactFormFieldset />
            </ModalBody>
            <ModalFooter px={0}>
              <Stack
                w="full"
                justifyContent="space-between"
                flexWrap="wrap"
                spacing={8}
                direction={{ base: 'column', sm: 'row' }}
              >
                <Button colorScheme="black" size="sm" onClick={onClose}>
                  {t('contactForm.actions.close')}
                </Button>
                <SpeakerContactFormSubmit size="sm">{t('contactForm.actions.send')}</SpeakerContactFormSubmit>
              </Stack>
            </ModalFooter>
          </SpeakerContactForm>

          <ModalCloseButton />
        </ModalContent>
      </Modal>
    </Fragment>
  );
};
